import type { NineSliceSprite, Sprite } from 'pixi.js';
import { Container } from 'pixi.js';
import last from 'lodash/last';
import { RecyclableCullingView } from './RecyclableCullingView';
import { WinStreakPathNodeCheckType, WinStreakPathNodeView } from './WinStreakPathNodeView';
import { RecyclableSpritePiece } from './RecyclableSpritePiece';
import { WinStreakChestView } from './WinStreakChestView';
import { WinStreakPlayerView } from './WinStreakPlayerView';
import type { WinStreakRoadMapData } from '../types';

interface WinStreakRoadViewConfig {
  roadMapData: WinStreakRoadMapData;
  userProgress: number;
  mask: Sprite | NineSliceSprite;
}

export class WinStreakRoadView extends Container {
  private readonly pathLinkLayer = new Container().props({
    parent: this,
    eventMode: 'none',
  });

  private readonly pathTopLayer = new Container().props({
    parent: this,
    eventMode: 'none',
  });

  private readonly decorations = new Container().props({
    parent: this,
    eventMode: 'none',
  });

  private readonly chestLayer = new Container().props({
    parent: this,
  });

  private readonly scrollView = new RecyclableCullingView(this);

  private readonly nodeViews: WinStreakPathNodeView[] = [];

  private readonly chestViews: WinStreakChestView[] = [];

  private readonly sprites: RecyclableSpritePiece[] = [];

  private readonly playerView: WinStreakPlayerView;

  constructor(private readonly config: WinStreakRoadViewConfig) {
    super();

    this.addPathNodes();
    this.addPathLinks();
    this.addChests();
    this.addDecorations();
    this.playerView = this.createPlayerView();
  }

  private createPlayerView(): WinStreakPlayerView {
    const {
      userProgress,
      roadMapData: { pathNodes },
    } = this.config;

    const pathNode = pathNodes[userProgress] ?? pathNodes[pathNodes.length - 1];

    return new WinStreakPlayerView(userProgress).props({ x: pathNode.x, y: pathNode.y, parent: this });
  }

  private addPathNodes(): void {
    const { roadMapData, userProgress } = this.config;

    roadMapData.pathNodes.forEach((pathNodeData, pathIndex) => {
      let checkMarkType = WinStreakPathNodeCheckType.None;

      if (pathNodeData.checkMark) {
        checkMarkType = WinStreakPathNodeCheckType.Green;
      } else if (pathIndex <= userProgress - 1) {
        checkMarkType = WinStreakPathNodeCheckType.Yellow;
      }

      const view = new WinStreakPathNodeView({
        pathLayer: this.pathTopLayer,
        checkMarkType,
        x: pathNodeData.x,
        y: pathNodeData.y,
      });

      this.nodeViews.push(view);
      this.scrollView.addPiece(view, pathNodeData.y, 200);
    });
  }

  private addPathLinks(): void {
    const { roadMapData } = this.config;
    roadMapData.pathNodes.forEach((pathNodeData, index, array) => {
      const prevNode = array[index - 1];
      if (!prevNode) return;

      let y = (pathNodeData.y + prevNode.y) / 2;
      let texture = 'Hexagon_Path_Link_Vertical';
      let scaleX = 1;
      if (pathNodeData.x !== prevNode.x) {
        texture = 'Hexagon_Path_Link_Diagonal';
        scaleX = pathNodeData.x < prevNode.x ? 1 : -1;
        y += 10;
      }

      const piece = new RecyclableSpritePiece({
        x: (pathNodeData.x + prevNode.x) / 2,
        y,
        texture,
        scaleX,
        anchor: 0.5,
        parent: this.pathLinkLayer,
      });

      this.scrollView.addPiece(piece, y, 100);

      this.sprites.push(piece);
    });
  }

  private addChests(): void {
    const {
      userProgress,
      roadMapData: { chests },
    } = this.config;

    chests.forEach((data) => {
      const view = new WinStreakChestView({
        root: this.chestLayer,
        opened: data.level <= userProgress,
        chestData: data,
      });
      this.chestViews.push(view);
      view.onClicked.connect(() => {
        view.toggleBubbleReward();
      });
      this.scrollView.addPiece(view, data.y, 400);
    });
  }

  public hideAllRewardBubbles(): void {
    this.chestViews.forEach((chestView) => {
      chestView.turnOffBubbleReward();
    });
  }

  public getChestView(index: number): WinStreakChestView | null {
    return this.chestViews[index] ?? null;
  }

  private addDecorations(): void {
    const {
      roadMapData: { decorations },
    } = this.config;

    decorations.forEach((decorationProps) => {
      const view = new RecyclableSpritePiece({
        ...decorationProps,
        parent: this.decorations,
        zIndex: decorationProps.y,
      });
      this.scrollView.addPiece(view, decorationProps.y ?? 0, 400);
    });
    this.decorations.sortableChildren = true;
  }

  public setNodeCheckmark(level: number, checkMarkType: WinStreakPathNodeCheckType): void {
    void this.nodeViews[level]?.changeCheckMark(checkMarkType);
  }

  public update(): void {
    this.scrollView.update(this.config.mask.getBounds());
  }

  public getHeight(): number {
    return Math.abs(last(this.config.roadMapData.pathNodes)?.y ?? 0) + 400;
  }

  public getPlayerView(): WinStreakPlayerView {
    return this.playerView;
  }
}
